import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { mostrarAlertaError } from '@/helpers/alert';
import api from '@/providers/api';
import { subDays } from 'date-fns';
import { ACTIVO, CARGANDO, FILTRADO, INACTIVO } from '../../constantes';
import { PARAMETROS_DOCUMENTOS } from '@/helpers/urls';
import { sonObjetosIguales } from '@/helpers/objects';
import { filtrarFacturas } from '@/redux/slices/dteSlice/filtrosListadoDocumentos';
import { Parametro } from '@/models/respuestas/parametro.model';
import Dte from '@/models/respuestas/dte.model';
import { parseRangeToSerializable } from '@/helpers/date';
import { RangoSerializable } from '@/models/componentes/datePicker.model';

export type DeudorSeleccion = {
  rznSocial: string;
  rut: string;
};

export type TiposFiltrosDocumentos = {
  folio: string;
  emision: RangoSerializable;
  deudor: DeudorSeleccion;
};

export type CargaUtilFiltrosDtesCliente = {
  filtro: keyof TiposFiltrosDocumentos;
  carga: TiposFiltrosDocumentos[keyof TiposFiltrosDocumentos];
};

export const deudorPredeterminado: DeudorSeleccion = {
  rznSocial: '',
  rut: 'Todos los deudores',
};

export const filtrosIniciales = {
  folio: '',
  emision: null,
  deudor: deudorPredeterminado,
} as TiposFiltrosDocumentos;

export const crearFiltros = createAsyncThunk('filtros/crear', async () => {
  try {
    // Creacion de filtros iniciales tomando en cuenta parametros comerciales
    const hoy = new Date();
    const peticion = await api.get(PARAMETROS_DOCUMENTOS);
    const parametros = peticion.data.resultado as Parametro[];

    const diasAntiguedad =
      Number(
        parametros?.find((p) => p.codigo === 'DIAS_ANTIGUEDAD_FACTURAS')?.valor
      ) || 15;

    // La emision debe ser parseada en ISO o cualquier formato de fecha en string para ser serializable
    const emision = parseRangeToSerializable({
      from: subDays(hoy, diasAntiguedad),
      to: hoy,
    });

    const filtros: TiposFiltrosDocumentos = {
      folio: '',
      emision,
      deudor: deudorPredeterminado,
    };

    return { parametros, filtros };
  } catch {
    mostrarAlertaError('Hubo un error al cargar los parametros');

    return {
      parametros: [],
      filtros: {
        folio: '',
        emision: {},
        deudor: deudorPredeterminado,
      } as TiposFiltrosDocumentos,
    };
  }
});

export const estadoInicialDteSlice = {
  dtes: [] as Dte[],
  filtrados: [] as Dte[],
  estado: INACTIVO,
  filtrosIniciales: null as TiposFiltrosDocumentos,
  filtrosActuales: null as TiposFiltrosDocumentos,
  parametrosCom: null as Parametro[],
};

const dteSlice = createSlice({
  name: 'dtes',
  initialState: estadoInicialDteSlice,
  reducers: {
    // Action para copiar los Dtes de la API para poder ser filtrados
    cargarDtes: (state, action) => {
      return {
        ...state,
        estado: ACTIVO,
        dtes: action.payload,
        filtrados: action.payload,
      };
    },
    filtrarDtes: (
      state,
      { payload }: PayloadAction<CargaUtilFiltrosDtesCliente>
    ) => {
      const { filtrosIniciales, dtes, filtrosActuales } = state;
      const { carga, filtro } = payload;
      const facturasActuales = JSON.parse(JSON.stringify(dtes));

      const nuevosFiltros = JSON.parse(
        JSON.stringify({ ...filtrosActuales, [filtro]: carga })
      ) as TiposFiltrosDocumentos;

      // Verifico si los filtros pasados son iguales a los predeterminados
      const sonFiltrosPredeterminados =
        nuevosFiltros.folio === filtrosIniciales.folio &&
        sonObjetosIguales(nuevosFiltros.emision, filtrosIniciales.emision) &&
        sonObjetosIguales(nuevosFiltros.deudor, filtrosIniciales.deudor);

      let nuevoEstado;
      let facturas;

      if (sonFiltrosPredeterminados) {
        nuevoEstado = ACTIVO;
        facturas = facturasActuales;
      }
      if (!sonFiltrosPredeterminados) {
        nuevoEstado = FILTRADO;
        facturas = filtrarFacturas(facturasActuales, nuevosFiltros);
      }

      return {
        ...state,
        filtrados: facturas,
        estado: nuevoEstado,
        filtrosActuales: {
          ...nuevosFiltros,
        },
      };
    },
    // Action utilizada solamente cuando se quieren eliminar los filtros
    limpiarFiltros: (state) => {
      return {
        ...state,
        estado: ACTIVO,
        filtrados: state.dtes,
        filtrosActuales: { ...state.filtrosIniciales },
      };
    },
    // Action ejecutada cuando se refresca el listado para evitar inconvenientes con los filtros
    refrescarLista: (state) => {
      return {
        ...state,
        filtrosActuales: { ...state.filtrosIniciales },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(crearFiltros.pending, (state) => {
        state.estado = CARGANDO;
      })
      .addCase(crearFiltros.fulfilled, (state, { payload }) => {
        const { parametros, filtros } = payload;

        state.estado = ACTIVO;
        state.parametrosCom = parametros;
        state.filtrosActuales = filtros;
        state.filtrosIniciales = filtros;
      });
  },
});

export const { cargarDtes, filtrarDtes, limpiarFiltros, refrescarLista } =
  dteSlice.actions;
export default dteSlice.reducer;
